import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CalendarCheck, Warning, CheckCircle, Clock, ArrowsClockwise, ListChecks, Calendar as CalIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

const URGENCY = {
  overdue: { color: "bg-red-50 border-red-200 text-red-800", dot: "bg-red-500", label: "Overdue" },
  urgent: { color: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-500", label: "≤ 3 days" },
  upcoming: { color: "bg-sky-50 border-sky-200 text-sky-800", dot: "bg-sky-500", label: "≤ 10 days" },
  planned: { color: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500", label: "Planned" },
};
const KIND_LABEL = { pf: "PF ECR", esic: "ESIC", pt: "Prof. Tax", tds: "TDS" };

export default function ComplianceCalendar() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [months, setMonths] = useState(3);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { months };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/compliance-calendar", { params })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, months]);

  const buckets = useMemo(() => {
    const b = { overdue: [], urgent: [], upcoming: [], planned: [] };
    rows.forEach((r) => { (b[r.urgency] = b[r.urgency] || []).push(r); });
    return b;
  }, [rows]);

  const monthGroups = useMemo(() => {
    const g = {};
    rows.forEach((r) => { (g[r.deadline.slice(0, 7)] = g[r.deadline.slice(0, 7)] || []).push(r); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Compliance · Statutory</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Compliance calendar</h1>
          <p className="text-sm text-zinc-500 mt-2">Upcoming India statutory deadlines · PF (15th) · ESIC (15th) · PT (20th) · TDS (7th).</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={months} onChange={(e) => setMonths(parseInt(e.target.value))} className="border border-zinc-300 rounded-sm text-sm px-2 py-1.5 bg-white" data-testid="cal-months">
            {[1, 2, 3, 6, 12].map((n) => <option key={n} value={n}>{n} months</option>)}
          </select>
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="cal-refresh"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Overdue" count={buckets.overdue.length} tone="red" icon={<Warning size={14} />} />
        <SummaryCard label="Due in 3 days" count={buckets.urgent.length} tone="amber" icon={<Clock size={14} />} />
        <SummaryCard label="Due in 10 days" count={buckets.upcoming.length} tone="sky" icon={<CalendarCheck size={14} />} />
        <SummaryCard label="Planned" count={buckets.planned.length} tone="emerald" icon={<ListChecks size={14} />} />
      </div>

      <Tabs defaultValue="list">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="list" className="rounded-sm" data-testid="cal-tab-list"><ListChecks size={14} className="mr-1" /> List</TabsTrigger>
          <TabsTrigger value="month" className="rounded-sm" data-testid="cal-tab-month"><CalIcon size={14} className="mr-1" /> By month</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <div className="border border-zinc-200 rounded-sm overflow-x-auto bg-white" data-testid="cal-list-table">
            <table className="hr-table">
              <thead>
                <tr><th></th><th>Tenant</th><th>Filing</th><th>Period</th><th>Deadline</th><th>Days left</th><th>Status</th></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="text-center py-6 text-sm text-zinc-500">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-zinc-500">No compliance items in this window.</td></tr>}
                {!loading && rows.map((r, i) => {
                  const u = URGENCY[r.urgency] || URGENCY.planned;
                  return (
                    <tr key={i} data-testid={`cal-row-${r.tenant_id}-${r.kind}-${r.period_month}`}>
                      <td><span className={`inline-block w-2 h-2 rounded-full ${u.dot}`}></span></td>
                      <td className="text-sm">{r.tenant_name || "—"}</td>
                      <td><Badge variant="outline" className="rounded-sm font-mono-plex text-[10px]">{KIND_LABEL[r.kind] || r.kind.toUpperCase()}</Badge> <span className="ml-1.5 text-xs text-zinc-500">{r.label}</span></td>
                      <td className="font-mono-plex text-xs">{r.period_month}</td>
                      <td className="font-mono-plex text-xs">{r.deadline}</td>
                      <td className="font-mono-plex text-xs">
                        <Badge variant="outline" className={`rounded-sm ${u.color}`}>
                          {r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue` : `${r.days_left}d`}
                        </Badge>
                      </td>
                      <td>
                        <StatusPill status={r.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="month" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cal-month-grid">
            {monthGroups.map(([m, list]) => (
              <div key={m} className="border border-zinc-200 rounded-sm bg-white">
                <div className="px-3 py-2 border-b border-zinc-100 bg-adb-navy/5 flex items-center gap-2">
                  <CalIcon size={14} className="text-adb-navy" />
                  <div className="font-display font-bold text-sm">{new Date(m + "-01").toLocaleDateString("en", { month: "long", year: "numeric" })}</div>
                  <Badge variant="outline" className="rounded-sm ml-auto text-[10px] font-mono-plex">{list.length}</Badge>
                </div>
                <div className="divide-y divide-zinc-100">
                  {list.map((r, i) => {
                    const u = URGENCY[r.urgency] || URGENCY.planned;
                    return (
                      <div key={i} className="px-3 py-2 flex items-center gap-2 text-sm">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${u.dot} shrink-0`}></span>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono-plex text-xs text-zinc-500">{r.deadline}</div>
                          <div className="text-sm truncate"><span className="font-semibold">{KIND_LABEL[r.kind] || r.kind}</span> · {r.tenant_name || "—"}</div>
                        </div>
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${u.color}`}>{r.days_left < 0 ? "overdue" : `${r.days_left}d`}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ label, count, tone, icon }) {
  const tones = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`border rounded-sm p-3 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display text-3xl font-bold">{count}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    not_started: { label: "Not started", cls: "bg-zinc-50 border-zinc-200 text-zinc-700" },
    payroll_ready: { label: "Payroll ready", cls: "bg-sky-50 border-sky-200 text-sky-800" },
    generated: { label: "Generated", cls: "bg-adb-navy/5 border-adb-navy/20 text-adb-navy" },
    filed: { label: "Filed", cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  };
  const m = map[status] || { label: status, cls: "bg-zinc-50 border-zinc-200 text-zinc-700" };
  return <Badge variant="outline" className={`rounded-sm ${m.cls}`}>{m.label === "Filed" && <CheckCircle size={10} className="mr-1 inline" />}{m.label}</Badge>;
}
